/* eslint-disable react/require-default-props */
import React, { useRef } from 'react';
import classNames from 'classnames';
import { Popover, OverlayTrigger } from 'react-bootstrap';

import './Button.scss';
import { Placement } from 'react-bootstrap/esm/Overlay';

export type ButtonTypes = 'button' | 'submit' | 'reset' | 'file';

export interface ButtonProps {
  children: React.ReactNode;
  href?: string;
  btnStyle?: string;
  outline?: string;
  size?: string;
  onClick?: (e?: React.MouseEvent<HTMLElement>) => void;
  onFileSelect?: (e?: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  active?: boolean;
  toggled?: boolean;
  className?: string;
  tooltipId?: string;
  tooltip?: string;
  tooltipOnClick?: boolean;
  tooltipPlacement?: Placement;
  showTooltip?: boolean;
  unstyledIcon?: boolean;
  role?: string;
  type?: ButtonTypes;
  title?: string;
  showSpinner?: boolean;
  overlaySpinner?: boolean;
  noStyling?: boolean;
  form?: string;
  accept?: string;
}

const Button = React.forwardRef<
  HTMLAnchorElement | HTMLSpanElement | HTMLButtonElement,
  ButtonProps
>(
  (
    {
      className,
      disabled,
      size,
      outline,
      children,
      btnStyle,
      active,
      onClick,
      onFileSelect,
      toggled,
      tooltipId,
      tooltip,
      tooltipOnClick,
      tooltipPlacement,
      showTooltip,
      unstyledIcon,
      showSpinner,
      overlaySpinner,
      noStyling,
      href,
      accept,
      type,
      ...props
    },
    ref,
  ) => {
    const isFileInput = type === 'file';
    const fileInput = useRef<HTMLInputElement>(null);

    const Component = href ? 'a' : unstyledIcon ? 'span' : 'button';

    const styleClass = unstyledIcon ? 'material-icons' : null;

    const sizeClass = size ? `btn-${size}` : null;
    const otherProps: any = {};
    if (href) {
      otherProps.role = 'button';
    } else if (type === 'submit') {
      otherProps.type = 'submit';
    } else {
      otherProps.type = 'button';
    }

    const componentFunction = (data?: any) => {
      const { ...triggerHandler } = data || {};
      return (
        <Component
          ref={ref}
          {...triggerHandler}
          {...props}
          {...otherProps}
          // The presence of an onClick function prevents the
          // tooltip from displaying on click. If you are having
          // an issue with a disabled button showing a tooltip on
          // click, ensure that it is both disabled and has 'tooltipOnClick'
          // set to true.
          {...(!disabled && {
            onClick: (event) => {
              if (isFileInput) {
                fileInput.current?.click();
              } else if (onClick) {
                onClick(event);
              }
            },
          })}
          className={classNames(
            'Button',
            styleClass,
            sizeClass,
            {
              active,
              disabled,
              toggled,
              'no-styling': noStyling,
              'has-spinner': showSpinner,
            },
            className,
          )}
          href={href}
        >
          {showSpinner && (
            <div
              className={classNames('Button-spinner-wrapper', {
                'Button-overlay-spinner': overlaySpinner,
              })}
            >
              <div className="Button-spinner" />
            </div>
          )}
          {children}
          {isFileInput && (
            <input
              className="Button-file-input"
              ref={fileInput}
              accept={accept}
              onChange={(e) => {
                if (onFileSelect) {
                  onFileSelect(e);
                }
              }}
              type="file"
            />
          )}
        </Component>
      );
    };

    if (tooltip) {
      const id = tooltipId || `tooltip-${tooltip.replace(/ /g, '-')}`;
      return (
        <OverlayTrigger
          key={id}
          placement={tooltipPlacement || 'bottom'}
          show={showTooltip}
          transition={false}
          trigger={tooltipOnClick ? 'click' : ['hover', 'focus']}
          rootClose={tooltipOnClick}
          overlay={
            <Popover id={id} className="Button__Tooltip">
              <Popover.Content>{tooltip}</Popover.Content>
            </Popover>
          }
        >
          {(data) => componentFunction(data)}
        </OverlayTrigger>
      );
    }

    return componentFunction();
  },
);

export default Button;
